// The Recovery loop's daily action: declare a bedtime, keep it, the ring verifies. Plus the
// consistency run — the RS grind made visible.
import { useState } from "react";
import { Moon } from "lucide-react";
import { useLogEvent } from "../lib/api";
import type { Board } from "../lib/types";
import { Button, Chip } from "./ui";

const TIERS: [number, number][] = [
  [1, 1.0],
  [3, 1.2],
  [7, 1.5],
  [14, 2.0],
  [30, 2.5],
];

export function multiplierFor(run: number) {
  let m = 1.0;
  for (const [n, mult] of TIERS) if (run >= n) m = mult;
  return m;
}
export function nextTier(run: number) {
  return TIERS.find(([n]) => n > run)?.[0] ?? null;
}

function tomorrowLocal(boardDate: string) {
  const d = new Date(boardDate + "T12:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function Tonight({
  board,
  userId,
  sleepToday,
}: {
  board: Board;
  userId: string;
  sleepToday: number | null;
}) {
  const log = useLogEvent(userId);
  const [time, setTime] = useState("22:30");
  const run = board.sleep_run ?? 0;
  const mult = multiplierFor(run);
  const next = nextTier(run);
  const declared = board.bedtime_target ? new Date(board.bedtime_target) : null;

  function declare() {
    // Target is tonight at the chosen local time; the night's sleep belongs to tomorrow's date.
    const [h, m] = time.split(":").map(Number);
    const target = new Date();
    target.setHours(h, m, 0, 0);
    if (target.getTime() < Date.now() - 6 * 3600_000) target.setDate(target.getDate() + 1);
    void log.mutateAsync({
      type: "bedtime.declared",
      occurred_at: new Date().toISOString(),
      payload: {
        for_date: tomorrowLocal(board.date),
        target_at: target.toISOString(),
        target_local: time,
      },
    });
  }

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Moon size={16} className="text-recovery" />
          <h2 className="text-base font-semibold">Tonight</h2>
        </div>
        <Chip tone={run > 0 ? "recovery" : "mute"}>
          {run} night{run === 1 ? "" : "s"} at 7 h+ · {mult.toFixed(1)}×
        </Chip>
      </div>
      <p className="mt-1 text-xs text-ink-mute">
        {sleepToday != null
          ? `Last night ${(sleepToday / 3600).toFixed(1)} h${sleepToday >= 25200 ? " — counted." : " — under seven, no XP, run reset."}`
          : "Last night's sleep hasn't landed yet."}
        {next ? ` Next tier at ${next} nights.` : " Top tier."}
      </p>

      {board.can_declare_bedtime ? (
        declared ? (
          <p className="mt-3 text-sm text-ink-soft">
            Bedtime declared for{" "}
            <span className="numeral text-recovery">
              {declared.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
            . In bed within twenty minutes and the ring will credit it.
          </p>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <input
              type="time"
              className="field w-auto focus:field-focus"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
            <Button onClick={declare} disabled={log.isPending}>
              Declare bedtime
            </Button>
          </div>
        )
      ) : (
        <p className="mt-3 text-xs text-ink-mute">Declaring a bedtime unlocks at Recovery 3.</p>
      )}
    </section>
  );
}
